import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, TextField, Alert, MenuItem, CircularProgress, Card, Stack, Divider } from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';

const API_URL = import.meta.env.VITE_SERVICE_URL_HOSTLY_API;
const API_KEY = import.meta.env.VITE_API_SECRET_KEY;

interface PendingItem {
  id: string;
  productId: string;
  plan: string;
  billingInterval: string;
  originalTransactionId: string;
  transactionId: string;
  purchaseDate?: any;
  expiresDate?: any;
  notificationType?: string;
  status: string;
}

const PRODUCT_OPTIONS = [
  { id: '12', label: 'Hostly Basic Mensal' },
  { id: '1212', label: 'Hostly Basic Anual' },
  { id: '13', label: 'Hostly Pro Mensal' },
  { id: '1313', label: 'Hostly Pro Anual' },
];

export const AdminAppleSubscriptions: React.FC = () => {
  const { theme } = useTheme();
  const [pending, setPending] = useState<PendingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [linkInputs, setLinkInputs] = useState<Record<string, string>>({});
  const [linkingId, setLinkingId] = useState<string | null>(null);

  const [manualEmail, setManualEmail] = useState('');
  const [manualProductId, setManualProductId] = useState('1313');
  const [manualTransactionId, setManualTransactionId] = useState('');
  const [manualLoading, setManualLoading] = useState(false);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/apple-subscriptions/pending`, {
        headers: { 'X-API-Key': API_KEY },
      });
      const data = await res.json();
      setPending(data.items || []);
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || 'Erro ao carregar' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPending(); }, []);

  const linkToEmail = async (originalTransactionId: string) => {
    const email = linkInputs[originalTransactionId]?.trim();
    if (!email) {
      setMessage({ type: 'error', text: 'Informe o email do usuário' });
      return;
    }
    setLinkingId(originalTransactionId);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/admin/apple-subscriptions/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
        body: JSON.stringify({ originalTransactionId, email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao linkar');
      setMessage({ type: 'success', text: `Vinculado ao user ${data.userId}` });
      fetchPending();
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setLinkingId(null);
    }
  };

  const applyManual = async () => {
    if (!manualEmail.trim() || !manualProductId) {
      setMessage({ type: 'error', text: 'Email e produto obrigatórios' });
      return;
    }
    setManualLoading(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/admin/apple-subscriptions/apply-manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': API_KEY },
        body: JSON.stringify({
          email: manualEmail.trim(),
          productId: manualProductId,
          transactionId: manualTransactionId.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao aplicar');
      setMessage({ type: 'success', text: `Plano ${data.plan} (${data.billingInterval}) aplicado` });
      setManualEmail('');
      setManualTransactionId('');
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setManualLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ color: theme.colors.text.primary, mb: 1 }}>
        Assinaturas Apple
      </Typography>
      <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 3 }}>
        Pendentes vêm dos webhooks da Apple quando uma compra é feita sem identificação de usuário (ex: resgate de offer code via link externo). Vincule pelo email do cliente.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card sx={{ p: 2, mb: 3, backgroundColor: theme.colors.card, border: `1px solid ${theme.colors.border}` }}>
        <Typography variant="subtitle1" sx={{ color: theme.colors.text.primary, mb: 2, fontWeight: 600 }}>
          Aplicar Manualmente (compra já confirmada na App Store Connect)
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Email do cliente"
            value={manualEmail}
            onChange={e => setManualEmail(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            select
            label="Produto"
            value={manualProductId}
            onChange={e => setManualProductId(e.target.value)}
            fullWidth
            size="small"
          >
            {PRODUCT_OPTIONS.map(p => (
              <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Transaction ID (opcional)"
            value={manualTransactionId}
            onChange={e => setManualTransactionId(e.target.value)}
            fullWidth
            size="small"
          />
          <Button
            variant="contained"
            onClick={applyManual}
            disabled={manualLoading}
            sx={{ minWidth: 140, backgroundColor: theme.colors.primary }}
          >
            {manualLoading ? <CircularProgress aria-label="Carregando" size={20} /> : 'Aplicar'}
          </Button>
        </Stack>
      </Card>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
          Pendentes ({pending.length})
        </Typography>
        <Button onClick={fetchPending} size="small">Atualizar</Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress aria-label="Carregando" size={28} />
        </Box>
      ) : pending.length === 0 ? (
        <Typography variant="body2" sx={{ color: theme.colors.text.secondary, textAlign: 'center', py: 4 }}>
          Nenhuma assinatura pendente.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {pending.map(item => (
            <Card key={item.id} sx={{ p: 2, backgroundColor: theme.colors.card, border: `1px solid ${theme.colors.border}` }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                    {PRODUCT_OPTIONS.find(p => p.id === item.productId)?.label || `Produto ${item.productId}`}
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.colors.text.secondary, display: 'block' }}>
                    OrigTxn: {item.originalTransactionId} · Tipo: {item.notificationType || '-'}
                  </Typography>
                </Box>
                <TextField
                  size="small"
                  label="Email do cliente"
                  value={linkInputs[item.originalTransactionId] || ''}
                  onChange={e => setLinkInputs({ ...linkInputs, [item.originalTransactionId]: e.target.value })}
                  sx={{ minWidth: 240 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={linkingId === item.originalTransactionId}
                  onClick={() => linkToEmail(item.originalTransactionId)}
                  sx={{ backgroundColor: theme.colors.primary }}
                >
                  {linkingId === item.originalTransactionId ? <CircularProgress aria-label="Carregando" size={18} /> : 'Vincular'}
                </Button>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
};
